import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Languages } from "lucide-react";
import { toast } from "sonner";
import { mapDatabaseError } from "@/lib/errorUtils";

type Specialty = {
  id: string;
  name: string;
  name_fr: string | null;
  display_order: number;
  created_at: string;
};

const SpecialtiesAdmin = () => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Specialty | null>(null);
  const [name, setName] = useState("");
  const [nameFr, setNameFr] = useState("");

  const { data: specialties, isLoading } = useQuery({
    queryKey: ["admin-specialties"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("specialties")
        .select("*")
        .order("name");
      if (error) throw error;
      return data as Specialty[];
    },
  });

  // Count how many talent use each specialty
  const { data: talentData } = useQuery({
    queryKey: ["admin-talent-specialties-usage"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("talent")
        .select("specialty");
      if (error) throw error;
      return data;
    },
  });

  const usageMap = new Map<string, number>();
  talentData?.forEach((t) => {
    t.specialty?.split(",").map((s: string) => s.trim()).filter(Boolean).forEach((tag: string) => {
      usageMap.set(tag, (usageMap.get(tag) || 0) + 1);
    });
  });

  const saveMutation = useMutation({
    mutationFn: async ({ id, name, name_fr }: { id?: string; name: string; name_fr: string }) => {
      let finalNameFr = name_fr || null;

      // Auto-translate if no French name provided
      if (!name_fr) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const resp = await supabase.functions.invoke("lovable-ai-gateway", {
              headers: {
                Authorization: `Bearer ${session.access_token}`,
              },
              body: {
                model: "google/gemini-3-flash-preview",
                messages: [
                  {
                    role: "user",
                    content: `Translate this English specialty name to French. Return ONLY the French translation, nothing else.\n\n${name}`,
                  },
                ],
                temperature: 0.2,
                max_tokens: 100,
              },
            });
            const translated = resp.data?.choices?.[0]?.message?.content?.trim();
            if (translated) finalNameFr = translated;
          }
        } catch {
          // Translation failed silently — save without it
        }
      }

      if (id) {
        const oldSpecialty = editing?.name;
        const { error } = await supabase
          .from("specialties")
          .update({ name, name_fr: finalNameFr })
          .eq("id", id);
        if (error) throw error;

        if (oldSpecialty && oldSpecialty !== name) {
          const { data: affected } = await supabase
            .from("talent")
            .select("id, specialty")
            .ilike("specialty", `%${oldSpecialty}%`);

          if (affected) {
            for (const t of affected) {
              const tags = t.specialty.split(",").map((s: string) => s.trim());
              const updated = tags.map((tag: string) => (tag === oldSpecialty ? name : tag)).join(", ");
              await supabase.from("talent").update({ specialty: updated }).eq("id", t.id);
            }
          }
        }
      } else {
        const { error } = await supabase
          .from("specialties")
          .insert({ name, name_fr: finalNameFr });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-specialties"] });
      queryClient.invalidateQueries({ queryKey: ["talent-specialties"] });
      queryClient.invalidateQueries({ queryKey: ["admin-talent"] });
      setDialogOpen(false);
      setEditing(null);
      setName("");
      setNameFr("");
      toast.success(editing ? "Specialty updated" : "Specialty added");
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (specialty: Specialty) => {
      // Remove from all talent records first
      const { data: affected } = await supabase
        .from("talent")
        .select("id, specialty")
        .ilike("specialty", `%${specialty.name}%`);

      if (affected) {
        for (const t of affected) {
          const tags = t.specialty.split(",").map((s: string) => s.trim()).filter((tag: string) => tag !== specialty.name);
          await supabase.from("talent").update({ specialty: tags.join(", ") }).eq("id", t.id);
        }
      }

      const { error } = await supabase.from("specialties").delete().eq("id", specialty.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-specialties"] });
      queryClient.invalidateQueries({ queryKey: ["talent-specialties"] });
      queryClient.invalidateQueries({ queryKey: ["admin-talent"] });
      toast.success("Specialty deleted");
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  const translateMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await supabase.functions.invoke("translate-specialties", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin-specialties"] });
      if (data?.updated === 0) {
        toast.info(data?.message || "All specialties already translated");
      } else {
        toast.success(data?.message || "Translation completed");
      }
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  const openCreate = () => {
    setEditing(null);
    setName("");
    setNameFr("");
    setDialogOpen(true);
  };

  const openEdit = (s: Specialty) => {
    setEditing(s);
    setName(s.name);
    setNameFr(s.name_fr || "");
    setDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    saveMutation.mutate({ id: editing?.id, name: name.trim(), name_fr: nameFr.trim() });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold">Specialties</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage the master list of talent specialties
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => translateMutation.mutate()}
            disabled={translateMutation.isPending}
          >
            {translateMutation.isPending ? (
              <Loader2 size={16} className="mr-2 animate-spin" />
            ) : (
              <Languages size={16} className="mr-2" />
            )}
            {translateMutation.isPending ? "Translating..." : "Translate to French"}
          </Button>
          <Button onClick={openCreate}>
            <Plus size={16} className="mr-2" />
            Add Specialty
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {specialties?.length === 0 && (
          <p className="text-muted-foreground text-center py-8">No specialties yet.</p>
        )}
        {specialties?.map((s) => (
          <Card key={s.id}>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{s.name}</span>
                  {s.name_fr && (
                    <span className="text-sm text-muted-foreground">/ {s.name_fr}</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Used by {usageMap.get(s.name) || 0} talent
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(s)}>
                  <Pencil size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    const count = usageMap.get(s.name) || 0;
                    const msg = count > 0
                      ? `This specialty is used by ${count} talent. Delete and remove from all?`
                      : "Delete this specialty?";
                    if (confirm(msg)) deleteMutation.mutate(s);
                  }}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Specialty" : "Add Specialty"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="specialty-name">Name (English) *</Label>
              <Input
                id="specialty-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Voice Over"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="specialty-name-fr">Name (French)</Label>
              <Input
                id="specialty-name-fr"
                value={nameFr}
                onChange={(e) => setNameFr(e.target.value)}
                placeholder="e.g. Voix hors champ"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpecialtiesAdmin;
